/**
 * Getting the prices a day is still missing (MDOT-25191): the call, and what a failed call means.
 *
 * It answers with a pricing map and nothing else — applying it to the grid is the builder's job,
 * which is what keeps this function testable without a composition and the builder free of
 * transport concerns. What to ask about is decided before it, by `planPricing`: a plan with nothing
 * missing answers from the rows' own memo without reaching the network.
 *
 * A failed call is not a separate path: a network error or a 5xx comes back as every REQUESTED
 * identifier unpriced, and goes through the same arm of `applyPricing` a genuine `UNPRICED` does.
 * The rows the plan already knew about are answered from the memo and survive it — a day priced
 * earlier in this edit cannot be struck out by a later call that fails. The day is kept either way:
 * it is in the header and in the URL, and the grid saying it could not be priced there beats
 * showing the previous day's numbers under the new day's label.
 */
import type { Currencies, InstrumentPricing } from "@eu.mdotm.api/sphere";
import type { PricingByIdentifier, PricingPlan } from "./pricing";
import { allUnpriced, pricingFromResponse } from "./pricing";
import { priceWithoutPortfolio } from "./pricingWithoutPortfolio";
import { marketDatePricingRepository } from "./repository";

export type RepriceRequest = {
	/** The portfolio the prices are asked of; absent while the composition belongs to none. */
	investmentUuid?: string;
	/** What to price in when there is no portfolio to read the currency from. */
	currency?: Currencies;
	/** The composition's AUM, which pricing without a portfolio sizes against. */
	aum?: number;
	/** The day to price at (`YYYY-MM-DD`). */
	date: string;
	/** What the composition already knows at that day, and what is left to ask about. */
	plan: PricingPlan;
};

export async function fetchPricing({
	investmentUuid,
	currency,
	aum,
	date,
	plan,
}: RepriceRequest): Promise<PricingByIdentifier> {
	const { known, missing } = plan;
	if (missing.length === 0) {
		return known;
	}

	try {
		const instruments = await priceMissing({ investmentUuid, currency, aum, date, identifiers: missing });
		return new Map([...known, ...pricingFromResponse(instruments, missing)]);
	} catch {
		return new Map([...known, ...allUnpriced(missing)]);
	}
}

/**
 * The portfolio's pricing endpoint when there is a portfolio. Without one — a composition being
 * created, or a draft — Cold War's sizing in the composition's currency for its AUM
 * (`./pricingWithoutPortfolio`), and a request missing either rejects.
 */
async function priceMissing(params: {
	investmentUuid?: string;
	currency?: Currencies;
	aum?: number;
	date: string;
	identifiers: string[];
}): Promise<InstrumentPricing[]> {
	const { investmentUuid, currency, aum, date, identifiers } = params;
	if (investmentUuid != null) {
		return marketDatePricingRepository.priceInstruments(investmentUuid, { identifiers, referenceDate: date });
	}
	if (currency == null || aum == null) {
		throw new Error("Pricing a composition that belongs to no portfolio needs its currency and AUM");
	}
	return priceWithoutPortfolio({ identifiers, referenceDate: date, currency, aum });
}
